import { produce } from 'immer';
import type { Reducer } from 'redux';
import type { AssetTagRelationshipsActions } from '~/ducks/asset-tag-relationships/actions';
import AssetTagRelationshipsConstants from '~/ducks/asset-tag-relationships/constants';
import type { AssetTagsActions } from './actions';
import * as actions from './actions';
import AssetTagsConstants from './constants';
import { findCategoryIndexById, updateTag } from './utils';
import { initialTagState, TagState } from './data/TagState';

const reducer: Reducer<TagState, AssetTagsActions | AssetTagRelationshipsActions> = (
  state = initialTagState,
  action
) => {
  const draftState = (draft) => {
    switch (action.type) {
      case AssetTagsConstants.CLEAR_TAG_ACTION_ERROR: {
        draft.tagActionError = undefined;
        draft.errorModalTitle = undefined;
        break;
      }

      case AssetTagsConstants.GET_AVAILABLE_TAGS_REQUEST:
        draft.isLoading = true;
        delete draft.availableTagCategories;
        delete draft.error;
        break;

      case AssetTagsConstants.GET_AVAILABLE_TAGS_REQUEST_SUCCESS:
        draft.isLoading = false;
        draft.availableTagCategories = action.response.map((tagCategory) => {
          const tags = tagCategory.tags.map((tag) => ({
            ...tag,
            isLoading: false,
          }));
          return { ...tagCategory, tags: tags };
        });
        break;

      case AssetTagRelationshipsConstants.ADD_ASSET_TAG_RELATIONSHIPS_REQUEST:
      case AssetTagRelationshipsConstants.REMOVE_ASSET_TAG_RELATIONSHIPS_REQUEST: {
        if (!draft.availableTagCategories) {
          return state;
        }

        updateTag(draft, action.payload.tagCategory.id, action.payload.tag.id, {
          isLoading: true,
        });
        return draft;
      }

      case AssetTagRelationshipsConstants.ADD_ASSET_TAG_RELATIONSHIPS_REQUEST_SUCCESS:
      case AssetTagRelationshipsConstants.ADD_ASSET_TAG_RELATIONSHIPS_REQUEST_ERROR:
      case AssetTagRelationshipsConstants.REMOVE_ASSET_TAG_RELATIONSHIPS_REQUEST_SUCCESS:
      case AssetTagRelationshipsConstants.REMOVE_ASSET_TAG_RELATIONSHIPS_REQUEST_ERROR: {
        if (!draft.availableTagCategories) {
          return state;
        }

        updateTag(draft, action.payload.tagCategory.id, action.payload.tag.id, {
          isLoading: false,
        });
        return draft;
      }

      case AssetTagsConstants.GET_AVAILABLE_TAGS_REQUEST_ERROR:
        draft.isLoading = false;
        draft.availableTagCategories = [];
        draft.error = action.error;
        break;

      // ######################## TAG CATEGORY ########################
      case AssetTagsConstants.CREATE_TAG_CATEGORY_REQUEST:
        if (!draft.availableTagCategories) {
          return state;
        }

        draft.availableTagCategories.push(action.payload.tmpTagCategory);
        break;

      case AssetTagsConstants.CREATE_TAG_CATEGORY_REQUEST_SUCCESS: {
        if (!draft.availableTagCategories) {
          return state;
        }

        const index = findCategoryIndexById(draft, action.payload.tmpTagCategory.id);
        draft.availableTagCategories[index] = action.response;
        break;
      }

      case AssetTagsConstants.CREATE_TAG_CATEGORY_REQUEST_ERROR:
        if (!draft.availableTagCategories) {
          return state;
        }

        draft.availableTagCategories = draft.availableTagCategories.filter(
          (tc) => tc.id !== action.payload.tmpTagCategory.id
        );
        draft.tagActionError = action.error;
        draft.errorModalTitle =
          'web.asset_list.tags_drawer.edit_tags.create_tag_category_error.title';
        break;

      case AssetTagsConstants.UPDATE_TAG_CATEGORY_REQUEST: {
        if (!draft.availableTagCategories) {
          return state;
        }

        const index = findCategoryIndexById(draft, action.payload.tagCategory.id);
        draft.availableTagCategories[index].name = action.payload.name;
        break;
      }

      case AssetTagsConstants.UPDATE_TAG_CATEGORY_REQUEST_SUCCESS: {
        if (!draft.availableTagCategories) {
          return state;
        }

        const categoryId = action.response.id;
        const index = findCategoryIndexById(draft, categoryId);
        draft.availableTagCategories[index] = action.response;
        break;
      }

      case AssetTagsConstants.UPDATE_TAG_CATEGORY_REQUEST_ERROR: {
        if (!draft.availableTagCategories) {
          return state;
        }

        const index = findCategoryIndexById(draft, action.payload.tagCategory.id);

        draft.availableTagCategories[index].name = action.payload.tagCategory.name;
        draft.tagActionError = action.error;
        draft.errorModalTitle =
          'web.asset_list.tags_drawer.edit_tags.update_tag_category_error.title';

        break;
      }

      case AssetTagsConstants.DELETE_TAG_CATEGORY_REQUEST:
        if (!draft.availableTagCategories) {
          return state;
        }

        draft.availableTagCategories = draft.availableTagCategories.filter(
          (category) => category.id !== action.payload.tagCategory.id
        );
        break;

      case AssetTagsConstants.DELETE_TAG_CATEGORY_REQUEST_SUCCESS: {
        break;
      }

      case AssetTagsConstants.DELETE_TAG_CATEGORY_REQUEST_ERROR:
        if (!draft.availableTagCategories) {
          return state;
        }

        draft.availableTagCategories.splice(
          action.payload.prevIndex,
          0,
          action.payload.tagCategory
        );
        draft.tagActionError = action.error;
        draft.errorModalTitle =
          'web.asset_list.tags_drawer.edit_tags.delete_tag_category_error.title';
        break;

      // ########################  TAG  ########################

      case AssetTagsConstants.CREATE_TAG_REQUEST: {
        if (!draft.availableTagCategories) {
          return state;
        }

        const { tmpTag } = action.payload;
        const categoryIndex = findCategoryIndexById(draft, tmpTag.tagCategoryId);
        draft.availableTagCategories[categoryIndex].tags.push(tmpTag);
        break;
      }

      case AssetTagsConstants.CREATE_TAG_REQUEST_SUCCESS: {
        const responseTag = action.response;
        const { tmpTag } = action.payload;
        updateTag(draft, responseTag.tagCategoryId, tmpTag.id, responseTag);
        break;
      }

      case AssetTagsConstants.CREATE_TAG_REQUEST_ERROR: {
        const { tmpTag } = action.payload;
        updateTag(draft, tmpTag.tagCategoryId, tmpTag.id);
        draft.tagActionError = action.error;

        draft.errorModalTitle = 'web.asset_list.tags_drawer.edit_tags.create_tag_error.title';
        break;
      }

      case AssetTagsConstants.UPDATE_TAG_REQUEST: {
        const { tag, name } = action.payload;
        updateTag(draft, tag.tagCategoryId, tag.id, { name });
        break;
      }

      case AssetTagsConstants.UPDATE_TAG_REQUEST_SUCCESS: {
        const responseTag = action.response;
        updateTag(draft, responseTag.tagCategoryId, responseTag.id, responseTag);
        break;
      }

      case AssetTagsConstants.UPDATE_TAG_REQUEST_ERROR: {
        const { tag } = action.payload;
        updateTag(draft, tag.tagCategoryId, tag.id, tag);
        draft.tagActionError = action.error;

        draft.errorModalTitle = 'web.asset_list.tags_drawer.edit_tags.update_tag_error.title';
        break;
      }

      case AssetTagsConstants.DELETE_TAG_REQUEST: {
        const { tag } = action.payload;
        updateTag(draft, tag.tagCategoryId, tag.id);
        break;
      }

      case AssetTagsConstants.DELETE_TAG_REQUEST_SUCCESS:
        break;

      case actions.SET_IS_EDITING:
        draft.isEditing = action.payload;
        break;

      case AssetTagsConstants.DELETE_TAG_REQUEST_ERROR: {
        if (!draft.availableTagCategories) {
          return state;
        }

        const { tag } = action.payload;
        const categoryIndex = findCategoryIndexById(draft, tag.tagCategoryId);
        draft.availableTagCategories[categoryIndex].tags.splice(action.payload.prevIndex, 0, tag);
        draft.tagActionError = action.error;

        draft.errorModalTitle = 'web.asset_list.tags_drawer.edit_tags.delete_tag_error.title';
        break;
      }

      default:
        return state;
    }
  };
  return produce(state, draftState);
};

export default reducer;
